import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { upcomingEvents } from "@/mocks/upcomingEvents";
import RegistrationForm from "./components/RegistrationForm";
import Navbar from "@/pages/home/components/Navbar";
import CTAFooterSection from "@/pages/home/components/CTAFooterSection";

interface UpcomingEvent {
  id: number;
  title: string;
  description: string;
  image_url: string;
  event_datetime: string;
  location: string;
  max_spots: number;
  active: boolean;
  registrations_closed: boolean;
  unique_link_slug: string;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/New_York",
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/New_York",
  });
}

export default function UpcomingEventPage() {
  const { slug } = useParams<{ slug: string }>();
  const [event, setEvent] = useState<UpcomingEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [expandedImage, setExpandedImage] = useState(false);

  useEffect(() => {
    async function loadEvent() {
      setLoading(true);
      // Try Supabase first
      const { data, error } = await supabase
        .from("upcoming_events")
        .select("id, title, description, image_url, event_datetime, location, max_spots, active, registrations_closed, unique_link_slug")
        .eq("unique_link_slug", slug)
        .eq("active", true)
        .single();

      if (!error && data) {
        setEvent(data as UpcomingEvent);

        // Fetch approved registration count for sold-out check
        const { data: approvedCount, error: regError } = await supabase.rpc(
          "event_approved_count",
          { p_event_id: data.id }
        );

        if (!regError && typeof approvedCount === "number") {
          setRegisteredCount(approvedCount);
        }
      } else {
        // Fallback to mock data
        const mockEvent = upcomingEvents.find((e) => e.unique_link_slug === slug);
        if (mockEvent) {
          setEvent(mockEvent as UpcomingEvent);
        } else {
          setNotFound(true);
        }
      }
      setLoading(false);
    }

    if (slug) loadEvent();
  }, [slug]);

  // 11/02/2026: o folder curto (link.allbridgesolutions.pro/criadas) manda para #inscricao — a página carrega os dados
  // antes de desenhar o formulário, então o navegador não acha a âncora sozinho.
  useEffect(() => {
    if (loading || window.location.hash !== "#inscricao") return;
    requestAnimationFrame(() => document.getElementById("inscricao")?.scrollIntoView({ block: "start" }));
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Carregando...</p>
        </div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brown/10 flex items-center justify-center">
            <i className="ri-calendar-2-line text-2xl text-brown/40" />
          </div>
          <h2 className="font-display text-xl text-brown mb-2">Evento não encontrado</h2>
          <p className="font-body text-sm text-brown/60 mb-6">
            Este evento pode não estar mais disponível ou o link pode estar incorreto.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  const isSoldOut = event && registeredCount >= event.max_spots;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero section for the event */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-beige">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl">
            {/* Event image — with expand/collapse on mobile */}
            <div
              className={`relative w-full rounded-t-2xl transition-all duration-500 ease-in-out ${
                expandedImage ? "h-auto" : "h-52 overflow-hidden"
              } md:h-80 md:overflow-hidden`}
            >
              <img
                src={event.image_url}
                alt={event.title}
                className={`w-full object-cover object-top transition-transform duration-700 ${
                  expandedImage ? "" : "h-full"
                }`}
              />
              {/* Fade overlay — only when collapsed on mobile to hint more content below */}
              {!expandedImage && (
                <div className="md:hidden absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
              )}
            </div>

            {/* Expand/collapse button — mobile only */}
            <button
              onClick={() => setExpandedImage(!expandedImage)}
              className="md:hidden flex items-center gap-2 mx-auto mt-3 px-5 py-2.5 rounded-full bg-white/95 backdrop-blur-sm border border-primary/30 text-primary font-body text-xs tracking-wide shadow-sm transition-all duration-300 hover:bg-primary/5 hover:border-primary/50 whitespace-nowrap cursor-pointer"
            >
              {expandedImage ? (
                <>
                  Ver menos
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-up-s-line text-sm" />
                  </div>
                </>
              ) : (
                <>
                  Ver folder completo
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-sm" />
                  </div>
                </>
              )}
            </button>

            <div className="p-6 md:p-10">
              <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
                Próximo Evento
              </p>
              <h1 className="font-display text-2xl md:text-3xl text-brown mb-6">
                {event.title}
              </h1>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-beige/60">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <i className="ri-calendar-line text-primary" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-brown/40 uppercase tracking-wide mb-0.5">Data e Hora</p>
                    <p className="font-body text-sm text-brown">
                      {formatDate(event.event_datetime)}
                      <br />
                      às {formatTime(event.event_datetime)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-beige/60">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-primary" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-brown/40 uppercase tracking-wide mb-0.5">Local</p>
                    <p className="font-body text-sm text-brown">{event.location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-beige/60">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-primary" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-brown/40 uppercase tracking-wide mb-0.5">Vagas</p>
                    <p className="font-body text-sm text-brown">{event.max_spots} vagas</p>
                  </div>
                </div>
              </div>

              <div className="prose prose-sm max-w-none">
                <h3 className="font-display text-lg text-brown mb-3">Sobre o evento</h3>
                <p className="font-body text-sm text-brown/70 leading-relaxed">
                  {event.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sold-out waiting list warning */}
      {isSoldOut && (
        <section className="py-0 bg-beige">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 border-2 border-primary/25 p-px">
              <div className="rounded-2xl bg-gradient-to-br from-primary/5 via-white to-accent/5 p-5 md:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {/* Big icon with glow */}
                  <div className="relative shrink-0">
                    <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
                    <div className="relative w-14 h-14 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center">
                      <i className="ri-vip-crown-line text-2xl text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    {/* Strong badge */}
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-white font-body text-[11px] font-bold tracking-wider uppercase mb-2">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-sparkling-line text-xs" />
                      </div>
                      Vagas preenchidas
                    </div>
                    <p className="font-body text-sm text-brown font-semibold mb-1">
                      Todas as vagas deste evento já foram reservadas.
                    </p>
                    <p className="font-body text-[13px] text-brown/60 leading-snug">
                      Você ainda pode se inscrever na lista de espera, aqui embaixo. Se alguma das {event.max_spots} vagas abrir,
                      chamamos pela ordem de chegada, pelo WhatsApp.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Registration form */}
      <RegistrationForm isSoldOut={isSoldOut} registrationsClosed={event.registrations_closed} />

      <CTAFooterSection />
    </div>
  );
}